import React from "react";
import {
  Button as MuiButton,
  ButtonProps as MuiButtonProps,
  CircularProgress,
} from "@mui/material";
import { styled } from "@mui/material/styles";

export type ButtonVariant = "primary" | "secondary" | "outline" | "text" | "danger";
export type ButtonSize = "small" | "medium" | "large";

export interface ButtonProps extends Omit<
  MuiButtonProps,
  "variant" | "size" | "color"
> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  fullWidth?: boolean;
}

const StyledButton = styled(MuiButton, {
  shouldForwardProp: (prop) => prop !== "buttonVariant",
})<{ buttonVariant?: ButtonVariant }>(({ theme, buttonVariant }) => {
  const styles: Record<ButtonVariant, any> = {
    primary: {
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.primary.contrastText,
      "&:hover": { backgroundColor: theme.palette.primary.dark },
    },
    secondary: {
      backgroundColor: theme.palette.secondary.main,
      color: theme.palette.secondary.contrastText,
      "&:hover": { backgroundColor: theme.palette.secondary.dark },
    },
    outline: {
      border: `2px solid ${theme.palette.primary.main}`,
      color: theme.palette.primary.main,
      backgroundColor: "transparent",
      boxShadow: "none",
      "&:hover": {
        border: `2px solid ${theme.palette.primary.main}`,
        backgroundColor: "rgba(28, 33, 24, 0.05)",
      },
    },
    text: {
      color: theme.palette.primary.main,
      "&:hover": { backgroundColor: theme.palette.action.hover },
    },
    danger: {
      backgroundColor: theme.palette.error.main,
      color: theme.palette.error.contrastText,
      "&:hover": { backgroundColor: theme.palette.error.dark },
    },
  };

  return {
    ...styles[buttonVariant || "primary"],
    textTransform: "none",
    fontWeight: 700,
    letterSpacing: "0.015em",
    boxShadow: theme.shadows[1],
    transition: "all 200ms ease",
    "&:active": {
      transform: "scale(0.98)",
    },
    "&:disabled": {
      opacity: 0.6,
    },
  };
});

/**
 * Composant Button réutilisable basé sur la charte graphique
 *
 * @example
 * ```tsx
 * <Button variant="primary" size="large" onClick={handleClick}>
 *   Envoyer
 * </Button>
 *
 * <Button variant="secondary" loading={isLoading}>
 *   Chargement...
 * </Button>
 * ```
 */
export const Button: React.FC<ButtonProps> = ({
  variant = "primary",
  size = "medium",
  loading = false,
  children,
  disabled,
  ...props
}) => {
  const getMuiVariant = (): "contained" | "outlined" | "text" => {
    if (variant === "text") return "text";
    if (variant === "outline") return "outlined";
    return "contained";
  };

  const getMuiSize = (): "small" | "medium" | "large" => {
    if (size === "large") return "large";
    if (size === "small") return "small";
    return "medium";
  };

  return (
    <StyledButton
      buttonVariant={variant}
      variant={getMuiVariant()}
      size={getMuiSize()}
      disabled={disabled || loading}
      {...props}
    >
      {loading ? (
        <>
          <CircularProgress size={16} sx={{ mr: 1, color: "inherit" }} />
          {children}
        </>
      ) : (
        children
      )}
    </StyledButton>
  );
};
